import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FilePond, registerPlugin } from "react-filepond";
import { CampaignContext } from '../../context/CampaignContext';
import "filepond/dist/filepond.min.css";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import {
    createCampaignElement, fetchCampaignAssets,
    fetchCampaignElementSections, getCampaignElementTypes,
    updateElementSection
} from '../requests/Requests';
import InputWithAutosuggest from './DialogComponents/InputWithAutosuggest';
import { isZip } from '../requests/Helpers';
import StyledTextFiled from './DialogComponents/StyledTextFiled';
import ButtonCustomStyled from './DialogComponents/DialogActionButton';
import styles from '../../assets/css/dialogContent.module.css';
import VideoFramePicker from '../VideoFrameSelector';

registerPlugin(FilePondPluginImagePreview);

export default function DialogUploadFile(props) {
    const [files, setFiles] = useState([]);
    const [elementType, setElementType] = useState(undefined);
    const [elementTypeError, setElementTypeError] = useState(undefined);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedVideoFrame, setSelectedVideoFrame] = useState(null);

    const {
        openDialogUploadFile: openDialogUploadFileBase,
        selectedCampaign: selectedCampaignBase,
        campaignAssets: campaignAssetsBase,
        campaignElementTypes: campaignElementTypesBase,
        campaignAssetSections: campaignAssetSectionsBase,
        dialogEditAssetSections: dialogEditAssetSectionsBase
    } = useContext(CampaignContext);

    const [, setOpenDialogUploadFile] = openDialogUploadFileBase;
    const [selectedCampaign] = selectedCampaignBase;
    const [, setCampaignAssets] = campaignAssetsBase;
    const [campaignElementTypes, setCampaignElementTypes] = campaignElementTypesBase;
    const [campaignAssetSections, setCampaignAssetSections] = campaignAssetSectionsBase;
    const [, setDialogEditAssetSections] = dialogEditAssetSectionsBase;

    const { isEdit, sectionData } = props;

    useEffect(() => {
        if (isEdit && sectionData) {
            const type = campaignElementTypes.find(x => x.id === sectionData.sectionType);
            setComment(sectionData.comment);
            setElementType(type.name);
        } else {
            setComment("");
            setElementType(undefined);
        }
    }, []);

    const handleSetFile = async (fileItems) => {
        const mappedFiles = fileItems.map(fileItem => fileItem.file);
        setFiles(mappedFiles);
        setSelectedVideoFrame(null);
    };

    const videoFile = files.find(file => {
        const type = file?.type?.toLowerCase() || "";
        const name = file?.name?.toLowerCase() || "";
        return type === "video/mp4" || name.endsWith(".mp4");
    });

    const handleSelectElementType = (value) => {
        setElementType(value);
        const type = campaignElementTypes.find(x => x.name === value);
        if (type) {
            const assetSection = campaignAssetSections.find(x => x.sectionType === type.id);
            setComment(assetSection ? assetSection.comment : "");
        }
        document.getElementById("comment").focus();
    };

    const handleSave = () => {
        isEdit ? editSectionElements() : uploadManyFiles();
    };

    const editSectionElements = async () => {
        setLoading(true);
        const formData = composeRequestBody();
        await updateElementSection(formData);
        await updateState();
        setLoading(false);
    };

    const uploadManyFiles = async () => {
        setLoading(true);
        if (!validation()) {
            setLoading(false);
            return;
        }
        await handleSaveElement();
        await updateState();
    };

    const updateState = async () => {
        const _campaignElementTypes = await getCampaignElementTypes();
        setCampaignElementTypes(_campaignElementTypes);

        const campaignAssets = await fetchCampaignAssets(selectedCampaign.id);
        setCampaignAssets(campaignAssets);

        const _sections = await fetchCampaignElementSections(selectedCampaign.id);
        setLoading(false);
        setCampaignAssetSections(_sections);
        handleClose();
    };

    const composeRequestBody = () => {
        const formData = new FormData();
        formData.append("campaignId", selectedCampaign.id);
        formData.append("elementType", elementType);
        formData.append("comment", comment);

        files.forEach(file => formData.append("files", file));

        if (files.length > 0 && isZip(files[0])) {
            formData.append("isCompressed", true);
        }

        if (selectedVideoFrame?.timeSeconds != null) {
            //time seconds by default returns a double like 4.4 seconds. But the backend expects danish culture, so 4,4 instead. 
            //The backend interprets 4.4 as 44 seconds.
            formData.append("thumbnailTimeSeconds", selectedVideoFrame.timeSeconds.toString().replace(".", ","));
            formData.append("thumbnailFrameIndex", selectedVideoFrame.frameIndex.toString());
        }

        return formData;
    };

    const handleSaveElement = async () => {
        const formData = composeRequestBody();
        await createCampaignElement(formData);
    };

    const validation = () => {
        if (!elementType) {
            setElementTypeError("Vælg venlist et element type");
        } else {
            setElementTypeError(undefined);
        }
        return files.length > 0 && elementType;
    };

    const handleClose = () => {
        isEdit ? setDialogEditAssetSections(false) : setOpenDialogUploadFile(false);
    };

    return (
        <Dialog
            open={true}
            onClose={() => handleClose()}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    maxHeight: '90vh',
                    height: 'auto',
                }
            }}
        >
            <DialogTitle className={styles.uploadFileBody}>
                {isEdit ? "Rediger element" : "Opret et nyt element"}
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: "0px 40px 40px 40px",
                    overflowY: 'auto',
                }}
            >
                <div className={styles.uploadInputFields}>
                    <div>
                        <InputWithAutosuggest
                            suggestedOptions={campaignElementTypes.map(x => x.name)}
                            label="Elementtype"
                            handleSelect={(value) => handleSelectElementType(value)}
                            elementTypeError={elementTypeError}
                            value={elementType}
                            isEdit={isEdit}
                        />
                        <StyledTextFiled
                            value={comment}
                            disabled={false}
                            label="Kommentar"
                            onChange={(value) => setComment(value)}
                            text="comment"
                            id="comment"
                        />
                    </div>
                    <div>
                        <small>
                            Godkendte filtyper til upload: PNG, GIF, JPG, JPEG, MP4, PDF og zip-fil (som indeholder flere bannere).
                            <br />En zip-fil skal indeholde en index.html-fil. Eksempel på en zip-fil: zip-filen  &gt; mappe med bannernavn &gt; index.html.
                        </small>
                        <FilePond
                            allowImagePreview={true}
                            files={files}
                            onupdatefiles={(fileItems) => handleSetFile(fileItems)}
                            allowMultiple={true}
                            maxFiles={10}
                            imagePreviewHeight={400}
                            labelIdle='Træk og slip dit element, eller <span class="filepond--label-action">gennemse filer</span> (JPG, GIF, MP4, HTML)'
                            style={{ backgroundColor: "#000000" }}
                        />
                        {videoFile && (
                            <VideoFramePicker
                                file={videoFile}
                                fps={25}
                                width={320}
                                height={240}
                                onSelectFrame={(frame) => setSelectedVideoFrame(frame)}
                            />
                        )}
                    </div>
                </div>
            </DialogContent>
            <DialogActions style={{ padding: "0px 40px 40px 40px" }}>
                <ButtonCustomStyled
                    text="Annuller"
                    onClick={() => handleClose()}
                    disabled={loading}
                />
                <ButtonCustomStyled
                    text={loading ? "Indlæser..." : "Gem element"}
                    onClick={() => handleSave()}
                    primary={true}
                    disabled={loading}
                />
            </DialogActions>
        </Dialog>
    );
}